import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useTranslations } from '@ui/i18n';
import { useGraphQLContext } from '@ui/providers/GraphQLProvider';
import type { GraphQLClient } from 'graphql-request';
import { useMemo } from 'react';

import type { StatsMetricValue } from './types';

const query = `
query vnoSummedEarningSnapshots($startTimestamp: Int!, $endTimestamp: Int!) {
  vnoSummedEarningSnapshots(orderBy:date, first: 1000, orderDirection:desc, where: {date_gte: $startTimestamp, date_lte: $endTimestamp}) {
    id
    totalEarning
    fountainEarning
    gardenEarning
    date
  }
}
`;

type Response = {
  vnoSummedEarningSnapshots: {
    id: string;
    totalEarning: string;
    fountainEarning: string;
    gardenEarning: string;
    date: string;
  }[];
};

const getQuery = (
  graphQlClient: GraphQLClient,
  startTimestamp: number,
  endTimestamp: number,
) => ({
  queryKey: ['useStatsClaimedVno', startTimestamp, endTimestamp],
  queryFn: async () => {
    const result = await graphQlClient.request<Response>(query, {
      startTimestamp,
      endTimestamp,
    });
    return result;
  },
});

export const useStatsClaimedVno = (
  startTimestamp: number,
  endTimestamp: number,
  options?: UseQueryOptions<Response>,
): StatsChartProps => {
  const client = useGraphQLContext().venoEarningsClient;
  const t = useTranslations();

  const queryResults = useQuery({
    ...getQuery(client, startTimestamp, endTimestamp),
    ...options,
  }).data;
  const result = useMemo(() => {
    if (!queryResults)
      return {
        totalEarning: undefined,
        fountainEarning: undefined,
        gardenEarning: undefined,
      };

    const arrays = {
      totalEarning: fillMissingDates(
        queryResults.vnoSummedEarningSnapshots.map((s) => ({
          timestamp: s.date,
          value: parseFloat(s.totalEarning),
        })),
      ).reverse(),
      fountainEarning: fillMissingDates(
        queryResults.vnoSummedEarningSnapshots.map((s) => ({
          timestamp: s.date,
          value: parseFloat(s.fountainEarning),
        })),
      ).reverse(),
      gardenEarning: fillMissingDates(
        queryResults.vnoSummedEarningSnapshots.map((s) => ({
          timestamp: s.date,
          value: parseFloat(s.gardenEarning),
        })),
      ).reverse(),
    };
    return arrays;
  }, [queryResults]);

  return useMemo(
    () => ({
      title: t('VNO Earnings'),
      values: {
        [t('totalEarning')]: result.totalEarning,
        [t('gardenEarning')]: result.gardenEarning,
        [t('fountainEarning')]: result.fountainEarning,
      },
      type: 'line',
    }),
    [result, t],
  );
};

function fillMissingDates(data: StatsMetricValue[]): StatsMetricValue[] {
  const filledData: StatsMetricValue[] = [];

  if (data.length === 0) return filledData; // Empty data check

  const currentDate = new Date(parseInt(data[0].timestamp) * 1000);
  let currentIndex = 0;

  while (currentIndex < data.length) {
    filledData.push(data[currentIndex]);

    if (currentIndex + 1 < data.length) {
      const nextDataDate = new Date(
        parseInt(data[currentIndex + 1].timestamp) * 1000,
      );
      currentDate.setDate(currentDate.getDate() - 1);

      while (currentDate.getTime() > nextDataDate.getTime()) {
        filledData.push({
          timestamp: (currentDate.getTime() / 1000).toString(),
          value: data[currentIndex].value,
        });
        currentDate.setDate(currentDate.getDate() - 1);
      }
    }

    currentIndex++;
  }

  return filledData;
}
